import { Link, type Href } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, View, type TextStyle } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { listIdentityRequests, type IdentityRequestListStatus, type IdentityRequestView } from "@/src/control/api";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import { border, radius, spacing, surface, text } from "@/src/theme/tokens";
import {
  controlAccountTypeLabel,
  controlVerificationStatusLabel,
  formatControlDate,
  identityRequestKindLabel,
  identityRequestStatusLabel,
} from "@/src/control/presentation";

const breakableTextStyle: TextStyle | undefined = Platform.OS === "web"
  ? ({ overflowWrap: "anywhere", wordBreak: "break-word" } as unknown as TextStyle)
  : undefined;

const filters: { value: IdentityRequestListStatus; label: string }[] = [
  { value: "pending", label: "En attente" },
  { value: "all", label: "Toutes" },
  { value: "approved", label: "Approuvées" },
  { value: "rejected", label: "Refusées" },
  { value: "cancelled", label: "Annulées" },
];

export default function ControlIdentityScreen() {
  return <ControlAccessGate permission="identity.requests.read"><IdentityQueue /></ControlAccessGate>;
}

function IdentityQueue() {
  const [status, setStatus] = useState<IdentityRequestListStatus>("pending");
  const [items, setItems] = useState<IdentityRequestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const refresh = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    listIdentityRequests(status, 100)
      .then((value) => active && setItems(value))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt, status]);

  return (
    <ScrollView contentContainerStyle={styles.page}>
      <View style={styles.header}>
        <Text style={styles.kicker}>IDENTITÉ</Text>
        <Text style={styles.title}>Demandes d’identité</Text>
        <Text style={styles.subtitle}>Examine les demandes de vérification et d’identité institutionnelle avant de rendre une décision.</Text>
      </View>

      <ScrollView horizontal style={styles.filterRail} contentContainerStyle={styles.filters} showsHorizontalScrollIndicator={false}>
        {filters.map((filter) => (
          <Pressable
            accessibilityRole="tab"
            accessibilityState={{ selected: filter.value === status }}
            key={filter.value}
            onPress={() => setStatus(filter.value)}
            style={[styles.filter, filter.value === status && styles.filterActive]}
          >
            <Text style={[styles.filterText, filter.value === status && styles.filterTextActive]}>{filter.label}</Text>
          </Pressable>
        ))}
      </ScrollView>

      {!loading && !error ? <Text style={styles.count}>{items.length} demande{items.length === 1 ? "" : "s"} affichée{items.length === 1 ? "" : "s"}</Text> : null}
      {loading ? <ActivityIndicator color={text.brand} /> : null}
      {error ? <Pressable accessibilityRole="button" onPress={refresh} style={styles.state}><Text style={styles.stateTitle}>File indisponible</Text><Text style={styles.stateBody}>Impossible de charger les demandes. Réessayer.</Text></Pressable> : null}
      {!loading && !error && !items.length ? <Text style={styles.empty}>Aucune demande dans cet état.</Text> : null}
      {!loading && !error ? items.map((item) => <IdentityRow item={item} key={item.request.id} />) : null}
    </ScrollView>
  );
}

function IdentityRow({ item }: { item: IdentityRequestView }) {
  const request = item.request;
  const requester = item.requester;
  return (
    <Link href={`/control/identity/${request.id}` as Href} asChild>
      <Pressable accessibilityRole="link" style={styles.row}>
        <View style={styles.rowHeader}>
          <View style={styles.identity}>
            <Text style={[styles.rowTitle, breakableTextStyle]}>{requester?.displayName || requester?.username && `@${requester.username.replace(/^@/, "")}` || "Demandeur non renseigné"}</Text>
            {requester?.username ? <Text style={[styles.handle, breakableTextStyle]}>@{requester.username.replace(/^@/, "")}</Text> : null}
          </View>
          <StatusBadge status={request.status} />
        </View>
        <View style={styles.infoRow}>
          <Text style={[styles.kind, breakableTextStyle]}>{identityRequestKindLabel(request.kind)}</Text>
          <Text style={[styles.type, breakableTextStyle]}>{controlAccountTypeLabel(request.requesterAccountType || requester?.accountType)}</Text>
        </View>
        {requester?.verificationStatus ? <Text style={[styles.meta, breakableTextStyle]}>Vérification actuelle : {controlVerificationStatusLabel(requester.verificationStatus)}</Text> : null}
        {request.note ? <Text numberOfLines={2} style={[styles.note, breakableTextStyle]}>{request.note}</Text> : null}
        <View style={styles.footer}>
          <Text style={[styles.footerText, breakableTextStyle]}>Demandée le {formatControlDate(request.createdAt)}</Text>
          <Text style={styles.open}>Ouvrir →</Text>
        </View>
      </Pressable>
    </Link>
  );
}

function StatusBadge({ status }: { status: IdentityRequestView["request"]["status"] }) {
  return <Text style={[styles.badge, status === "approved" ? styles.badgeSuccess : status === "rejected" ? styles.badgeDanger : status === "cancelled" ? styles.badgeMuted : styles.badgeActive]}>{identityRequestStatusLabel(status)}</Text>;
}

const styles = StyleSheet.create({
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.lg },
  header: { gap: spacing.sm },
  kicker: { color: text.brand, fontSize: 12, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 30, fontWeight: "800" },
  subtitle: { color: text.secondary, lineHeight: 23, minWidth: 0 },
  filterRail: { flexGrow: 0, maxWidth: "100%", width: "100%" },
  filters: { gap: spacing.sm },
  filter: { borderColor: border.subtle, borderRadius: radius.pill, borderWidth: 1, minHeight: 44, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  filterActive: { backgroundColor: surface.selected, borderColor: border.brand },
  filterText: { color: text.secondary, fontWeight: "700" },
  filterTextActive: { color: text.brand },
  count: { color: text.muted, fontSize: 12 },
  state: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.xs, padding: spacing.lg },
  stateTitle: { color: text.primary, fontSize: 16, fontWeight: "800" },
  stateBody: { color: text.secondary },
  empty: { color: text.secondary, paddingVertical: spacing.lg },
  row: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.sm, minWidth: 0, padding: spacing.lg },
  rowHeader: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.md, justifyContent: "space-between" },
  identity: { flex: 1, flexShrink: 1, gap: spacing.xs, minWidth: 0 },
  rowTitle: { color: text.primary, fontSize: 16, fontWeight: "800", minWidth: 0 },
  handle: { color: text.secondary, fontSize: 12, minWidth: 0 },
  infoRow: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, minWidth: 0 },
  kind: { color: text.brand, fontWeight: "700", minWidth: 0 },
  type: { color: text.secondary, minWidth: 0 },
  meta: { color: text.secondary, minWidth: 0 },
  note: { color: text.primary, lineHeight: 20, minWidth: 0 },
  footer: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, justifyContent: "space-between", minWidth: 0, paddingTop: spacing.xs },
  footerText: { color: text.muted, flex: 1, fontSize: 12, minWidth: 0 },
  open: { color: text.brand, fontSize: 12, fontWeight: "800" },
  badge: { borderRadius: radius.pill, fontSize: 12, fontWeight: "800", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  badgeActive: { backgroundColor: surface.warning, color: text.brand },
  badgeSuccess: { backgroundColor: surface.success, color: text.info },
  badgeDanger: { backgroundColor: surface.danger, color: text.danger },
  badgeMuted: { backgroundColor: surface.selected, color: text.muted },
});
